import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useCreatePromo, type CreatePromoPayload, type PromoType } from '@/api/promos';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

const PROMO_TYPE_OPTIONS = [
  { label: 'Percentage', value: 'percentage' },
  { label: 'Fixed amount', value: 'fixed_amount' },
  { label: 'Credit', value: 'credit' },
];

const CURRENCY_OPTIONS = [
  { label: 'VND', value: 'VND' },
  { label: 'USD', value: 'USD' },
];

/** Build a local YYYY-MM-DD date string (Asia/Saigon-safe — no toISOString). */
function toLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Build a RFC3339 string from a YYYY-MM-DD input value (midnight local). */
function dateInputToRfc3339(val: string): string | undefined {
  if (!val) return undefined;
  const [y, m, d] = val.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  const dt = new Date(y, m - 1, d, 0, 0, 0);
  // Format as RFC3339: the backend parses time.RFC3339
  const pad = (n: number) => String(n).padStart(2, '0');
  const tzOffset = -dt.getTimezoneOffset();
  const sign = tzOffset >= 0 ? '+' : '-';
  const absOff = Math.abs(tzOffset);
  const offH = pad(Math.floor(absOff / 60));
  const offM = pad(absOff % 60);
  return `${y}-${pad(m)}-${pad(d)}T00:00:00${sign}${offH}:${offM}`;
}

interface FormState {
  code: string;
  name: string;
  description: string;
  promo_type: PromoType;
  discount_value: string;
  max_discount: string;
  currency: string;
  max_uses: string;
  max_uses_per_customer: string;
  valid_from: string;
  valid_until: string;
  min_order_amount: string;
  new_customers_only: boolean;
}

const INITIAL: FormState = {
  code: '',
  name: '',
  description: '',
  promo_type: 'percentage',
  discount_value: '',
  max_discount: '',
  currency: 'VND',
  max_uses: '',
  max_uses_per_customer: '1',
  valid_from: toLocalDate(new Date()),
  valid_until: '',
  min_order_amount: '',
  new_customers_only: false,
};

/**
 * `/promos/new` — Create a new promo code. Mirrors the legacy
 * `datacentral-client-v4` New form: basic info + discount settings + usage
 * limits. Dates built via getFullYear/getMonth/getDate (timezone-safe).
 */
export default function PromoNewPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const createPromo = useCreatePromo();

  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitError, setSubmitError] = useState('');

  const set = (key: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!form.code.trim()) { setSubmitError('Code is required.'); return; }
    if (!form.name.trim()) { setSubmitError('Name is required.'); return; }
    if (!form.discount_value || isNaN(Number(form.discount_value))) {
      setSubmitError('Discount value must be a number.');
      return;
    }

    const payload: CreatePromoPayload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      promo_type: form.promo_type,
      discount_value: Number(form.discount_value),
      currency: form.currency || 'VND',
      max_uses: form.max_uses ? Number(form.max_uses) : undefined,
      max_uses_per_customer: form.max_uses_per_customer
        ? Number(form.max_uses_per_customer)
        : 1,
      min_order_amount: form.min_order_amount
        ? Number(form.min_order_amount)
        : undefined,
      new_customers_only: form.new_customers_only,
      valid_from: dateInputToRfc3339(form.valid_from),
      valid_until: dateInputToRfc3339(form.valid_until),
    };

    if (form.promo_type === 'percentage' && form.max_discount) {
      payload.max_discount = Number(form.max_discount);
    }

    try {
      const res = await createPromo.mutateAsync(payload);
      toast.success('Promo created successfully');
      navigate(`/promos/${res.result.promo_id}`);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Failed to create promo.';
      setSubmitError(msg);
    }
  };

  const isPercentage = form.promo_type === 'percentage';

  return (
    <Container size="lg">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <PageHeader title="New promo" description="Create a discount code" />

        {submitError ? (
          <Alert tone="danger" title={submitError} />
        ) : null}

        <form onSubmit={onSubmit}>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {/* Basic information */}
            <Card>
              <CardHeader>
                <CardTitle>Basic information</CardTitle>
              </CardHeader>
              <CardBody>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: 'var(--vn-space-4)',
                  }}
                >
                  <Field label="Code" required description="Will be uppercased automatically">
                    {({ id }) => (
                      <Input
                        id={id}
                        value={form.code}
                        onChange={(e) => set('code', e.target.value)}
                        placeholder="e.g. NEWYEAR2025"
                        style={{ textTransform: 'uppercase' }}
                      />
                    )}
                  </Field>

                  <Field label="Name" required>
                    {({ id }) => (
                      <Input
                        id={id}
                        value={form.name}
                        onChange={(e) => set('name', e.target.value)}
                        placeholder="Promo display name"
                      />
                    )}
                  </Field>

                  <Field label="Description" style={{ gridColumn: '1 / -1' }}>
                    {({ id }) => (
                      <Input
                        id={id}
                        value={form.description}
                        onChange={(e) => set('description', e.target.value)}
                        placeholder="Optional description"
                      />
                    )}
                  </Field>
                </div>
              </CardBody>
            </Card>

            {/* Discount settings */}
            <Card>
              <CardHeader>
                <CardTitle>Discount settings</CardTitle>
              </CardHeader>
              <CardBody>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 'var(--vn-space-4)',
                  }}
                >
                  <Field label="Type" required>
                    {({ id }) => (
                      <Select
                        value={form.promo_type}
                        onValueChange={(v) => set('promo_type', v as PromoType)}
                      >
                        <SelectTrigger id={id}>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {PROMO_TYPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>

                  <Field
                    label={isPercentage ? 'Discount (%)' : 'Discount value'}
                    required
                  >
                    {({ id }) => (
                      <NumericInput
                        id={id}
                        min={0}
                        step={isPercentage ? 0.01 : 1}
                        value={form.discount_value}
                        onChange={(e) => set('discount_value', e.target.value)}
                        placeholder={isPercentage ? '10' : '50000'}
                      />
                    )}
                  </Field>

                  {isPercentage ? (
                    <Field label="Max discount cap" description="Leave blank for no cap">
                      {({ id }) => (
                        <NumericInput
                          id={id}
                          min={0}
                          value={form.max_discount}
                          onChange={(e) => set('max_discount', e.target.value)}
                          placeholder="Optional"
                        />
                      )}
                    </Field>
                  ) : null}

                  <Field label="Currency">
                    {({ id }) => (
                      <Select
                        value={form.currency}
                        onValueChange={(v) => set('currency', v)}
                      >
                        <SelectTrigger id={id}>
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCY_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>

                  <Field label="Min order amount" description="Leave blank for no minimum">
                    {({ id }) => (
                      <NumericInput
                        id={id}
                        min={0}
                        value={form.min_order_amount}
                        onChange={(e) => set('min_order_amount', e.target.value)}
                        placeholder="Optional"
                      />
                    )}
                  </Field>
                </div>
              </CardBody>
            </Card>

            {/* Usage limits */}
            <Card>
              <CardHeader>
                <CardTitle>Usage limits &amp; validity</CardTitle>
              </CardHeader>
              <CardBody>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 'var(--vn-space-4)',
                  }}
                >
                  <Field label="Max uses" description="Leave blank for unlimited">
                    {({ id }) => (
                      <NumericInput
                        id={id}
                        min={1}
                        value={form.max_uses}
                        onChange={(e) => set('max_uses', e.target.value)}
                        placeholder="Unlimited"
                      />
                    )}
                  </Field>

                  <Field label="Max uses per customer">
                    {({ id }) => (
                      <NumericInput
                        id={id}
                        min={1}
                        value={form.max_uses_per_customer}
                        onChange={(e) =>
                          set('max_uses_per_customer', e.target.value)
                        }
                      />
                    )}
                  </Field>

                  <Field label="Valid from">
                    {({ id }) => (
                      <DateInput
                        id={id}
                        value={form.valid_from}
                        onChange={(e) => set('valid_from', e.target.value)}
                      />
                    )}
                  </Field>

                  <Field label="Valid until" description="Leave blank for no expiry">
                    {({ id }) => (
                      <DateInput
                        id={id}
                        value={form.valid_until}
                        onChange={(e) => set('valid_until', e.target.value)}
                      />
                    )}
                  </Field>

                  <Field label="New customers only" style={{ gridColumn: '1 / -1' }}>
                    {({ id }) => (
                      <label
                        htmlFor={id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--vn-space-2)',
                          cursor: 'pointer',
                        }}
                      >
                        <Checkbox
                          id={id}
                          checked={form.new_customers_only}
                          onCheckedChange={(v) =>
                            set('new_customers_only', v === true)
                          }
                        />
                        <span
                          style={{
                            fontSize: 'var(--vn-font-size-sm)',
                            color: 'var(--vn-fg-default)',
                          }}
                        >
                          Restrict to new customers only
                        </span>
                      </label>
                    )}
                  </Field>
                </div>
              </CardBody>
            </Card>

            {/* Actions */}
            <div
              style={{
                display: 'flex',
                gap: 'var(--vn-space-2)',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="ghost"
                type="button"
                onClick={() => navigate('/promos')}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={createPromo.isPending}
              >
                Create promo
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Container>
  );
}
